import { AlertTriangle } from "lucide-react";

import type { Artifact } from "@/api/artifacts";
import { shortHash } from "@/lib/hash-display";

interface FailedUpstreamBannerProps {
  /**
   * The selected artifacts whose `scientific_status === "failed"`,
   * keyed by the input port that received them. When empty the
   * banner doesn't render.
   */
  failedSelections: ReadonlyArray<{
    portName: string;
    artifact: Artifact;
  }>;
  /** Override-checkbox state, controlled by parent. */
  overrideChecked: boolean;
  /** Called when the user toggles the override checkbox. */
  onOverrideChange: (next: boolean) => void;
}

/**
 * M9 #60 slice 3: blocking banner shown above the Submit button when
 * one or more selected input artifacts carry
 * `scientific_status === "failed"`.
 *
 * Per `P1-FULL-AFFDO.md` §7.3: downstream Tiles refuse to consume
 * `failed`-tagged bundles without explicit override. The override
 * checkbox here threads through to the submit body's
 * `override_failed_upstream: true` field; the backend records the
 * decision in the audit log (per anchor §7.4 + #298).
 *
 * The Submit button's `disabled` state is owned by the parent
 * (SubmitTile) — this component only surfaces the warning + the
 * checkbox state.
 */
export function FailedUpstreamBanner({
  failedSelections,
  overrideChecked,
  onOverrideChange,
}: FailedUpstreamBannerProps): JSX.Element | null {
  if (failedSelections.length === 0) return null;

  const checkboxId = "ack-override-failed-upstream";

  return (
    <div
      role="alert"
      aria-labelledby="failed-upstream-heading"
      className="rounded-md border-l-4 border-state-failed-validation-fg bg-state-failed-validation-fg/5 p-4"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle
          className="mt-0.5 h-5 w-5 shrink-0 text-state-failed-validation-fg"
          aria-hidden
        />
        <div className="space-y-2">
          <h3
            id="failed-upstream-heading"
            className="text-sm font-semibold text-state-failed-validation-fg"
          >
            {failedSelections.length === 1
              ? "Selected upstream is failed-tagged"
              : `${failedSelections.length} selected upstreams are failed-tagged`}
          </h3>
          <p className="text-xs text-foreground">
            One or more input artifacts you selected carry{" "}
            <span className="font-mono">scientific_status: &quot;failed&quot;</span>. The backend
            refuses to consume them unless you explicitly override:
          </p>
          <ul className="space-y-1 text-xs">
            {failedSelections.map(({ portName, artifact }) => (
              <li key={portName} className="rounded bg-background/60 px-2 py-1 font-mono">
                <span className="font-semibold">{portName}</span>:{" "}
                <span title={artifact.content_hash}>{shortHash(artifact.content_hash)}</span>
                {artifact.producer_tile && (
                  <>
                    {" "}
                    from {artifact.producer_tile}
                    {artifact.run_id && (
                      <>
                        {" "}
                        (run <span className="underline">{artifact.run_id}</span>)
                      </>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
          <label htmlFor={checkboxId} className="flex items-start gap-2 pt-2 text-xs">
            <input
              id={checkboxId}
              type="checkbox"
              checked={overrideChecked}
              onChange={(e) => onOverrideChange(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-input"
            />
            <span>
              I acknowledge this upstream is failed-tagged and want to override the safety block.
              The override is logged for the audit trail.
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}
